import { makeStyles } from '@mui/styles';
import { Theme as DefaultTheme } from '@mui/material';
import { Color } from 'shared/utils/colors';

export const DEFAULT_THUMBNAIL_CARD_SIZE = 182;

export const listImageBarSx = {
  '&.MuiImageListItemBar-root': {
    cursor: 'pointer',
  },
  '& .MuiImageListItemBar-title': {
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export const listImageBarFullTitleSx = {
  '& .MuiImageListItemBar-title': {
    display: 'block',
    width: '100%',
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
  },
};

export const listImageBarActionsBottomSx = {
  '&.MuiImageListItemBar-root': {
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  '& .MuiImageListItemBar-titleWrap': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  '& .MuiImageListItemBar-actionIcon': {
    position: 'static',
    width: '100%',
    marginRight: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

export function listImageAnimatedActionIconSx(show: boolean): {
  overflow: string;
  maxHeight: string;
  opacity: number;
  transform: string;
  marginTop: string;
  paddingBottom: string;
  pointerEvents: 'none' | 'auto';
  transition: string;
} {
  return {
    overflow: 'hidden',
    maxHeight: show ? '56px' : '0px',
    opacity: show ? 1 : 0,
    transform: show ? 'translateY(0)' : 'translateY(4px)',
    marginTop: show ? '4px' : '0px',
    paddingBottom: show ? '4px' : '0px',
    pointerEvents: show ? 'auto' : 'none',
    transition:
      'max-height 180ms ease, opacity 180ms ease, transform 180ms ease, margin-top 180ms ease, padding-bottom 180ms ease',
  };
}

export const listImageActionsContainerSx = {
  display: 'flex',
  gap: '4px',
};

export const listImageSpinnerContainerSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: Color.solidGray,
  zIndex: 1000,
} as const;

export const listImageIconContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: Color.solidGray,
} as const;

export const listImageVideoContainerSx = {
  width: '100%',
  height: '100%',
  backgroundColor: Color.solidGray,
  overflow: 'hidden',
} as const;

export const listImageVideoPreviewSx = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
} as const;

export const graphicUploaderImageListSx = {
  width: '100%',
  marginBottom: '8px',
  marginTop: '8px',
} as const;

export function getListImageContentSx(
  imageOpacity: number
): {
  opacity: number;
  transition: string;
  width: string;
  height: string;
} {
  return {
    opacity: imageOpacity,
    transition: 'opacity 0.3s',
    width: '100%',
    height: '100%',
  };
}

export const linkedDocumentationAddTileSx = (thumbnailSize: number) => ({
  width: `${thumbnailSize}px !important`,
  height: `${thumbnailSize}px !important`,
  overflow: 'hidden',
  borderRadius: 0,
});

export const animatedAddTileSx = (
  thumbnailSize: number,
  compact: boolean,
  shouldAnimate: boolean
) => ({
  width: `${compact ? Math.round(thumbnailSize / 2) : thumbnailSize}px !important`,
  height: `${compact ? Math.round(thumbnailSize / 2) : thumbnailSize}px !important`,
  minWidth: `${compact ? Math.round(thumbnailSize / 2) : thumbnailSize}px !important`,
  minHeight: `${compact ? Math.round(thumbnailSize / 2) : thumbnailSize}px !important`,
  overflow: 'hidden',
  borderRadius: 0,
  transition: shouldAnimate
    ? 'width 220ms ease, height 220ms ease, min-width 220ms ease, min-height 220ms ease'
    : 'none',
});

export const linkedDocumentationAddTileButtonSx = {
  width: '100%',
  height: '100%',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  color: 'text.primary',
  backgroundColor: 'action.hover',
  transition: 'background-color 150ms ease, border-color 150ms ease',
  '&:hover': {
    backgroundColor: 'action.selected',
    color: 'text.primary',
  },
  '&:focus-visible': {
    outline: '1px solid',
    outlineColor: 'text.disabled',
  },
  '& .MuiSvgIcon-root': {
    transition: 'font-size 220ms ease',
  },
};

export function getListItemSx(
  thumbnailSize: number,
  withPosition = false
): {
  width: string;
  height: string;
  cursor: string;
  overflow: string;
  position?: 'relative';
} {
  return {
    width: `${thumbnailSize}px !important`,
    height: `${thumbnailSize}px !important`,
    cursor: 'pointer',
    overflow: 'hidden',
    ...(withPosition && { position: 'relative' as const }),
  };
}

export const selectedThumbnailOutlineSx = {
  outline: `3px solid ${Color.primaryOrange}`,
  outlineOffset: '-3px',
} as const;

export const focusedThumbnailSx = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    border: `3px solid ${Color.primaryOrange}`,
    zIndex: 1002,
    pointerEvents: 'none',
  },
} as const;

export const selectedThumbnailOverlaySx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: Color.primaryOrangeLight,
  zIndex: 999,
  pointerEvents: 'none',
} as const;

const styles = makeStyles<DefaultTheme>({
  image: {
    cursor: 'pointer',
    backgroundColor: Color.solidGray,
  },

  fileInput: {
    display: 'none',
  },
});

export default styles;
